import { BACKGROUND_PARAMS, drawBackground } from '../background';
import { scaleParamValues } from '../params/scale';
import { TEXT_LAYER_PARAMS } from '../params/textLayer';
import { applyPostChain, POST_EFFECT_BY_ID } from '../postfx/chain';
import { createCanvas, get2d } from '../text/rasterize';
import { drawText } from '../text/drawText';
import { buildTextField, type TextField } from '../text/textField';
import { textFieldCacheKey, type TextSettings } from '../text/settings';
import { buildTextSchema, toTextSettings } from '../text/textParams';
import type { LoadedFont } from '../fonts/fontLoader';
import { createRng } from '../rng';
import type { ParamValues } from '../params/types';
import { getTechnique, type RegisteredTechnique } from '../../techniques/registry';
import type { Ctx2D, RenderTarget, TechniqueContext } from '../../techniques/types';
import { blendToComposite, TEXT_TECHNIQUE_ID, type Layer } from '../../store/layer';
import type { StudioDocument } from '../../store/studioStore';

/**
 * One prepared composition, ready to be stepped and painted.
 *
 * This exists because the still-image exporter and the animation exporter need exactly
 * the same setup — text field at output resolution, per-layer scaled parameter values,
 * technique states, background and post-processing — and differ only in *when* they
 * paint. Pulling the setup out means an animation frame is genuinely the same pipeline
 * as an export, not a second implementation that drifts from it.
 *
 * The stage owns its canvas and simulation state, so a caller may step a little, paint,
 * step some more, and paint again.
 */
export interface StageLayer {
  layer: Layer;
  technique: RegisteredTechnique;
  context: TechniqueContext;
  state: unknown;
  steps: number;
}

/** Text fields are expensive; a sequence of frames that shares text geometry reuses one. */
export type FieldCache = Map<string, TextField>;

export interface StageOptions {
  doc: StudioDocument;
  font: LoadedFont;
  scale: number;
  fieldCache?: FieldCache;
  /** Reuse an existing canvas instead of allocating one per frame. */
  canvas?: OffscreenCanvas | HTMLCanvasElement;
}

export class RenderStage {
  readonly canvas: OffscreenCanvas | HTMLCanvasElement;
  readonly ctx: Ctx2D;
  readonly width: number;
  readonly height: number;
  readonly field: TextField;
  readonly textSettings: TextSettings;
  /** Technique layers only, in document order. */
  readonly layers: StageLayer[] = [];

  private readonly doc: StudioDocument;
  private readonly scale: number;
  private readonly background: ParamValues;
  private readonly textLayerValues: ParamValues;

  constructor(options: StageOptions) {
    const { doc, font, scale } = options;
    this.doc = doc;
    this.scale = scale;
    this.width = Math.round(doc.canvas.width * scale);
    this.height = Math.round(doc.canvas.height * scale);

    const textSchema = buildTextSchema(font);
    const scaledTextValues = scaleParamValues(textSchema, doc.textValues, scale);
    const textLayer = doc.layers.find((l) => l.techniqueId === TEXT_TECHNIQUE_ID);
    this.textLayerValues = textLayer ? scaleParamValues(TEXT_LAYER_PARAMS, textLayer.values, scale) : {};

    this.textSettings = toTextSettings(scaledTextValues, {
      fontId: doc.fontId,
      anchorX: doc.anchorX,
      anchorY: doc.anchorY,
      visible: textLayer?.visible ?? true,
      renderMode: (typeof this.textLayerValues.renderMode === 'string'
        ? this.textLayerValues.renderMode
        : 'fill') as TextSettings['renderMode'],
      strokeWidth: typeof this.textLayerValues.strokeWidth === 'number' ? this.textLayerValues.strokeWidth : 3,
    });

    const key = `${doc.fontId}|${textFieldCacheKey(this.textSettings, this.width, this.height)}`;
    const cached = options.fieldCache?.get(key);
    if (cached) {
      this.field = cached;
    } else {
      this.field = buildTextField({ settings: this.textSettings, font, width: this.width, height: this.height }).field;
      options.fieldCache?.set(key, this.field);
    }

    this.canvas =
      options.canvas && options.canvas.width === this.width && options.canvas.height === this.height
        ? options.canvas
        : createCanvas(this.width, this.height);
    this.ctx = get2d(this.canvas);

    this.background = scaleParamValues(BACKGROUND_PARAMS, doc.background, scale);

    for (const layer of doc.layers) {
      if (layer.techniqueId === TEXT_TECHNIQUE_ID) continue;
      if (!layer.visible || layer.opacity <= 0) continue;
      const technique = getTechnique(layer.techniqueId);
      if (!technique) continue;

      const context: TechniqueContext = {
        field: this.field,
        width: this.width,
        height: this.height,
        rng: createRng(layer.seed),
        values: scaleParamValues(technique.params, layer.values, scale),
        scale,
        // Exports always run at full budget; the preview's adaptive quality never
        // affects output.
        quality: 1,
      };
      this.layers.push({ layer, technique, context, state: technique.init(context), steps: 0 });
    }
  }

  /** Advances one layer by `steps` simulation steps. Stops early if it completes. */
  advance(entry: StageLayer, steps: number, dt = 1 / 60): void {
    for (let i = 0; i < steps; i++) {
      if (entry.technique.isComplete(entry.state)) return;
      entry.state = entry.technique.step(entry.state, dt, entry.context);
      entry.steps += 1;
    }
  }

  /**
   * Runs every layer to completion. The cap protects against techniques that never
   * report completion by design — they are animations, and an export has to stop
   * somewhere.
   */
  runToCompletion(maxSteps = 40000, onProgress?: (progress: number) => void): void {
    this.layers.forEach((entry, i) => {
      this.advance(entry, maxSteps);
      onProgress?.((i + 1) / this.layers.length);
    });
  }

  isComplete(): boolean {
    return this.layers.every((entry) => entry.technique.isComplete(entry.state));
  }

  /** Draws background, every layer in order, then the post-processing chain. */
  paint(): void {
    const { ctx, width, height } = this;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
    ctx.clearRect(0, 0, width, height);

    drawBackground(ctx, this.background, width, height, this.doc.seed);

    const target: RenderTarget = { ctx, width, height, scale: this.scale };
    const byId = new Map(this.layers.map((entry) => [entry.layer.id, entry]));

    for (const layer of this.doc.layers) {
      if (!layer.visible || layer.opacity <= 0) continue;

      ctx.save();
      ctx.globalAlpha = layer.opacity;
      ctx.globalCompositeOperation = blendToComposite(layer.blendMode);

      if (layer.techniqueId === TEXT_TECHNIQUE_ID) {
        const color = typeof this.textLayerValues.color === 'string' ? this.textLayerValues.color : '#f2f2f7';
        drawText(ctx, this.field, this.textSettings, color);
      } else {
        const entry = byId.get(layer.id);
        if (entry) entry.technique.draw(entry.state, target, entry.context);
      }

      ctx.restore();
    }

    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';

    // Post-processing runs on the scaled canvas with scaled lengths, so a bloom radius
    // covers the same proportion of the image at every export size.
    const scaledPost = { ...this.doc.postFx };
    for (const [id, entry] of Object.entries(this.doc.postFx)) {
      const effect = POST_EFFECT_BY_ID.get(id);
      if (!effect) continue;
      scaledPost[id] = { ...entry, values: scaleParamValues(effect.params, entry.values, this.scale) };
    }
    applyPostChain(ctx, width, height, scaledPost, this.doc.seed);
  }

  elementCount(): number {
    let total = 0;
    for (const entry of this.layers) total += entry.technique.elementCount?.(entry.state) ?? 0;
    return total;
  }
}
